// Agrupamento inteligente do pipeline por cidade × família de segmento (+ kWh/prioridade).
// Regra simples (client-side) sobre os leads já carregados em vw_mapa_leads.
import { MapaLead } from '../../types';

export type ClusterPrioridade = 'alta' | 'media' | 'baixa';

export interface LeadCluster {
    id: string;               // `${cidade}|${familia}`
    cidade: string;
    familia: string;
    leads: MapaLead[];
    kwhTotal: number;
    diasParado: number;       // max dias sem atividade do grupo
    prioridade: ClusterPrioridade;
}

const FAMILIAS: Array<{ familia: string; kws: string[] }> = [
    { familia: 'Mercados / Empórios', kws: ['mercado', 'supermerc', 'atacad', 'empório', 'emporio', 'hortifruti', 'verdur', 'mercearia', 'distribuidor'] },
    { familia: 'Padarias / Confeitarias', kws: ['padaria', 'confeitaria', 'panific'] },
    { familia: 'Açougues / Casas de Carne', kws: ['açougue', 'acougue', 'carne', 'frigorífic', 'frigorific'] },
    { familia: 'Hotéis / Condomínios', kws: ['hotel', 'pousada', 'motel', 'condomín', 'condomin', 'clube'] },
    { familia: 'Restaurantes / Bares', kws: ['restaurante', 'lanchonete', 'bar', 'botequim', 'pizzaria', 'sorveteria', 'bebida'] },
    { familia: 'Saúde', kws: ['farmácia', 'farmacia', 'drogaria', 'clínica', 'clinica', 'hospital', 'pet'] },
    { familia: 'Educação', kws: ['escola', 'colégio', 'colegio', 'faculdade', 'universidade'] },
    { familia: 'Academias / Esporte', kws: ['academia', 'esporte'] },
    { familia: 'Postos', kws: ['posto', 'combust'] },
    { familia: 'Indústria / Agro', kws: ['indústria', 'industria', 'fábrica', 'fabrica', 'agro', 'irriga', 'gelo'] },
];

/** Mapeia o segmento livre do lead para uma "família" exibível. */
export const segmentoFamilia = (seg?: string | null): string => {
    const s = (seg || '').toLowerCase();
    if (!s.trim()) return 'Outros';
    for (const f of FAMILIAS) {
        if (f.kws.some(k => s.includes(k))) return f.familia;
    }
    return 'Outros';
};

/** Cor (tailwind) da tag de segmento, p/ a UI dos cards. */
export const familiaTag = (familia: string): string => {
    switch (familia) {
        case 'Mercados / Empórios': return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
        case 'Padarias / Confeitarias': return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
        case 'Açougues / Casas de Carne': return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300';
        case 'Hotéis / Condomínios': return 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
        case 'Restaurantes / Bares': return 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
        case 'Saúde': return 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
        case 'Educação': return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
        default: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    }
};

const prioridadeDe = (kwhTotal: number, diasParado: number): ClusterPrioridade => {
    if (kwhTotal >= 30000 || diasParado >= 5) return 'alta';
    if (kwhTotal >= 8000 || diasParado >= 3) return 'media';
    return 'baixa';
};

const PRIO_PESO: Record<ClusterPrioridade, number> = { alta: 2, media: 1, baixa: 0 };

/**
 * Agrupa leads por cidade × família de segmento, calcula kWh total / dias parado / prioridade
 * e ordena por prioridade (desc) e kWh total (desc). Clusters de 1 lead são mantidos.
 */
export const clusterLeads = (leads: MapaLead[]): LeadCluster[] => {
    const mapa = new Map<string, LeadCluster>();
    for (const l of leads) {
        const cidade = (l.cidade || '').trim() || 'Sem cidade';
        const familia = segmentoFamilia(l.segmento);
        const id = `${cidade}|${familia}`;
        let c = mapa.get(id);
        if (!c) {
            c = { id, cidade, familia, leads: [], kwhTotal: 0, diasParado: 0, prioridade: 'baixa' };
            mapa.set(id, c);
        }
        c.leads.push(l);
        c.kwhTotal += Number(l.consumo_medio_kwh || 0);
        c.diasParado = Math.max(c.diasParado, Number(l.dias_sem_atividade || 0));
    }
    const clusters = Array.from(mapa.values());
    for (const c of clusters) {
        c.prioridade = prioridadeDe(c.kwhTotal, c.diasParado);
        // Dentro do cluster: maior kWh primeiro (quem visitar antes)
        c.leads.sort((a, b) => Number(b.consumo_medio_kwh || 0) - Number(a.consumo_medio_kwh || 0));
    }
    clusters.sort((a, b) =>
        (PRIO_PESO[b.prioridade] - PRIO_PESO[a.prioridade]) || (b.kwhTotal - a.kwhTotal)
    );
    return clusters;
};
